"use client";

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent,
} from "react";
import Image from "next/image";
import { ImageIcon } from "lucide-react";

export type ServiceSlide = {
  imageSrc: string;
  imageAlt: string;
  title: string;
  description?: string;
};

export type ServiceSliderProps = {
  slides: ServiceSlide[];
  autoPlayInterval?: number;
  className?: string;
};

function SlideImage({
  src,
  alt,
  title,
}: {
  src: string;
  alt: string;
  title: string;
}) {
  const [hasError, setHasError] = useState(false);

  if (hasError || !src) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center bg-linear-to-br from-[#29361a] to-[#1f2914] text-gray-400">
        <ImageIcon className="h-12 w-12" />
        <span className="mt-2 px-2 text-center text-xs">{title}</span>
      </div>
    );
  }

  return (
    <Image
      src={src}
      alt={alt}
      fill
      sizes="(max-width: 768px) 100vw, 480px"
      quality={70}
      className="object-cover transition-transform duration-500 ease-out group-hover:scale-105"
      onError={() => setHasError(true)}
    />
  );
}

export function ServiceSlider({
  slides,
  autoPlayInterval = 5000,
  className,
}: ServiceSliderProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const dragOffsetRef = useRef(0);
  const dragStateRef = useRef<{
    startX: number;
    active: boolean;
  } | null>(null);
  const viewportRef = useRef<HTMLDivElement | null>(null);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + slides.length) % slides.length);
  }, [slides.length]);

  const goToSlide = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  useEffect(() => {
    if (isHovered || isDragging || autoPlayInterval <= 0) return;

    const timer = setInterval(nextSlide, autoPlayInterval);
    return () => clearInterval(timer);
  }, [autoPlayInterval, isDragging, isHovered, nextSlide]);

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (slides.length <= 1) return;

    dragStateRef.current = {
      startX: event.clientX,
      active: false,
    };
    dragOffsetRef.current = 0;
    setIsDragging(true);
    setDragOffset(0);
    viewportRef.current?.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!dragStateRef.current) return;

    const deltaX = event.clientX - dragStateRef.current.startX;

    if (!dragStateRef.current.active && Math.abs(deltaX) > 6) {
      dragStateRef.current.active = true;
    }

    if (!dragStateRef.current.active) return;

    dragOffsetRef.current = deltaX;
    setDragOffset(deltaX);
  };

  const endDrag = (event: PointerEvent<HTMLDivElement>) => {
    if (!dragStateRef.current) return;

    const deltaX = dragOffsetRef.current;
    const threshold = 70;

    if (
      viewportRef.current?.hasPointerCapture(event.pointerId)
    ) {
      viewportRef.current.releasePointerCapture(event.pointerId);
    }

    if (deltaX > threshold) {
      prevSlide();
    } else if (deltaX < -threshold) {
      nextSlide();
    }

    dragStateRef.current = null;
    setIsDragging(false);
    setDragOffset(0);
  };

  const handlePointerCancel = (event: PointerEvent<HTMLDivElement>) => {
    endDrag(event);
  };

  const getVisibleSlides = () => {
    const visibleCount = 2;
    const result: { slide: ServiceSlide; index: number }[] = [];

    for (let i = 0; i < visibleCount; i++) {
      const index = (currentIndex + i) % slides.length;
      result.push({ slide: slides[index], index });
    }

    return result;
  };

  return (
    <div
      className={["relative", className].filter(Boolean).join(" ")}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        ref={viewportRef}
        className="cursor-grab select-none touch-pan-y active:cursor-grabbing"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        <div
          className="flex gap-4"
          style={{
            transform: `translate3d(${dragOffset}px, 0, 0)`,
            transition: isDragging ? "none" : "transform 420ms ease-in-out",
          }}
        >
        {getVisibleSlides().map(({ slide, index }) => (
          <div
            key={`${index}-${slide.title}`}
            className="group relative w-1/2 shrink-0 overflow-hidden rounded-2xl"
          >
            <div className="relative aspect-3/4 w-full">
              <SlideImage
                src={slide.imageSrc}
                alt={slide.imageAlt}
                title={slide.title}
              />
              <div className="absolute inset-0 bg-linear-to-t from-black/70 via-black/25 to-transparent transition-opacity duration-500 group-hover:from-black/80 group-hover:via-black/55" />
              <div className="absolute inset-0 bg-[#1f2914]/0 transition-colors duration-500 group-hover:bg-[#1f2914]/25" />
            </div>

            <div className="absolute inset-x-0 bottom-0 p-4">
              <h3 className="font-heading text-lg font-bold text-white">
                {slide.title}
              </h3>
              <div className="mt-3 max-h-0 overflow-hidden text-sm leading-relaxed text-white/90 opacity-0 transition-all duration-500 ease-in-out group-hover:max-h-40 group-hover:opacity-100">
                {slide.description}
              </div>
            </div>
          </div>
        ))}
        </div>
      </div>

      <div className="mt-6 flex items-center justify-center gap-2">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={[
              "h-2.5 w-2.5 rounded-full transition-all duration-300",
              index === currentIndex
                ? "bg-[#29361a] w-6"
                : "bg-gray-300 hover:bg-gray-400",
            ].join(" ")}
            aria-label={`Go to slide ${index + 1}`}
            aria-current={index === currentIndex ? "true" : undefined}
          />
        ))}
      </div>
    </div>
  );
}
